import { Grid, Paper, Stack } from "@mui/material";
import { Button } from "components/ButtonNew";
import { DataCard, dataCardUnitClassName, DataCardVariant, dataCardVariantStyles } from "components/DataCard";
import { Icon } from "components/Icon";
import { SuccessCheckmark } from "components/IconsTextsUtils";
import { LinearProgressWithLabel } from "components/LinearProgressWithLabel";
import { LoadingOrEmptyWrapper } from "components/LoadingAndEmptyHandler";
import { useGetBuildingJobInfo } from "features/dt-basic-setup/single-building/api/pipeline";
import { SingleBuildingContext } from "features/dt-basic-setup/single-building/context/AddSingleBuildingContext";
import React, { ReactNode, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ROUTE_PATHS } from "utils/routerDom";
import { cn } from "utils/styling";

import type { JobStatus } from "../../model/types";

import { REFETCH_INTERVAL, TIER_FEATURE_KEYS } from "./constants";

export const FinalStep = () => {
  const { t } = useTranslation();
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { formData, onStartNewBuilding } = useContext(SingleBuildingContext);
  const [refetchInterval, setRefetchInterval] = useState<boolean | number>(REFETCH_INTERVAL);

  const { data: jobInfo, isLoading } = useGetBuildingJobInfo(projectId, !!projectId, refetchInterval);

  const processingProgress = useMemo(() => {
    const n = Number(jobInfo?.progress ?? 0);
    return Math.min(100, Math.max(0, Number.isFinite(n) ? n : 0));
  }, [jobInfo]);

  useEffect(() => {
    const jobStatus = jobInfo?.status as JobStatus;
    if (jobStatus === "failed" || jobStatus === "completed") {
      setRefetchInterval(false);
    }
  }, [jobInfo]);

  const buildingName = formData?.addressInfo?.name?.trim() || t("AddBuilding.finalStep.defaultBuildingName");

  const goToBuildingProfile = useCallback(() => {
    navigate(ROUTE_PATHS.buildingProfile(projectId).navigateUrl ?? "/");
  }, [navigate, projectId]);

  const addAnotherBuilding = useCallback(() => {
    onStartNewBuilding();
  }, [onStartNewBuilding]);

  const statusCards: { id: number; labelKey: string; show: boolean; value: ReactNode; variant?: DataCardVariant }[] =
    useMemo(
      () => [
        {
          id: 0,
          labelKey: "AddBuilding.finalStep.status.analysesQueued",
          show: true,
          value: t("AddBuilding.finalStep.status.analysesCount"),
        },
        {
          id: 1,
          labelKey: "AddBuilding.finalStep.status.estCompletion",
          show: true,
          value: t("AddBuilding.finalStep.status.estTime"),
          variant: "warning",
        },
        {
          id: 2,
          labelKey: "AddBuilding.finalStep.status.processingLabel",
          show: jobInfo?.status === "completed",
          value: t("AddBuilding.finalStep.status.completedValue"),
          variant: "success",
        },
        {
          id: 3,
          labelKey: "AddBuilding.finalStep.status.processingLabel",
          show: jobInfo?.status === "failed",
          value: t("AddBuilding.finalStep.status.failedValue"),
          variant: "warning",
        },
        {
          id: 4,
          labelKey: "AddBuilding.finalStep.status.processingLabel",
          show: jobInfo?.status === "processing",
          value: (
            <Stack className="w-full min-w-0 gap-2">
              <LinearProgressWithLabel fullWidth hasAnimation value={processingProgress} />
              <span className={cn(dataCardUnitClassName, dataCardVariantStyles.info)}>
                {Math.round(processingProgress)}%
              </span>
            </Stack>
          ),
          variant: "info",
        },
      ],
      [jobInfo?.status, t, processingProgress]
    );

  return (
    <Grid item xs={12}>
      <div className="mx-auto w-full max-w-[110rem]">
        <Stack className="w-full flex-col gap-10">
          <LoadingOrEmptyWrapper showLoading={isLoading}>
            <Stack className="mx-auto w-full max-w-[40rem] flex-col text-center">
              <SuccessCheckmark />
              <h1 className="font-ds-headline-xl mt-6 ">{t("AddBuilding.finalStep.title")}</h1>
              <p className="mt-4 text-lds-foreground-muted font-ds-body-regular-md">
                {t("AddBuilding.finalStep.subtitle", { buildingName })}
              </p>
              <Button className="mt-8 gap-2" onClick={goToBuildingProfile} size="sm" variant="primary">
                {t("AddBuilding.finalStep.goToProfile")}
              </Button>
              <Button
                className="mt-4 text-lds-link-primary font-ds-body-regular-md"
                onClick={addAnotherBuilding}
                variant="tertiary"
              >
                {t("AddBuilding.finalStep.addAnotherBuilding")}
              </Button>
            </Stack>
            <div className="grid grid-cols-3 gap-4">
              {statusCards
                .filter((card) => card.show)
                .map(({ id, labelKey, value, variant }) => (
                  <DataCard
                    className="w-[22rem]"
                    key={id}
                    kpi={{
                      name: t(labelKey) ?? "",
                      value: value,
                    }}
                    variant={variant}
                  />
                ))}
            </div>
          </LoadingOrEmptyWrapper>
          <div className="mt-8 w-full">
            <h2 className="font-ds-headline-xl ">{t("AddBuilding.finalStep.tiers.heading")}</h2>
            <p className="mt-2 text-lds-foreground-muted font-ds-body-regular-md">
              {t("AddBuilding.finalStep.tiers.subtitle")}
            </p>
            <div className="mt-8 grid grid-cols-3 flex-nowrap gap-7">
              {Object.entries(TIER_FEATURE_KEYS).map(([tier, keys]) => (
                <Paper className="bg-lds-surface-bright p-6 border-ds-subtle" key={tier}>
                  <h3 className="font-ds-headline-xl ">{t(`AddBuilding.finalStep.tiers.${tier}.title`)}</h3>
                  <p className="mt-2 text-lds-foreground-muted font-ds-body-semibold-sm">
                    {t(`AddBuilding.finalStep.tiers.${tier}.description`)}
                  </p>
                  <Stack className="mt-6 flex-col items-start gap-3">
                    {keys.map((key) => (
                      <Stack className="gap-3" key={key}>
                        <Icon color="var(--color-element-primary-initial)" iconName="Checkmark" size="sm" />
                        <span className="font-ds-body-semibold-sm ">
                          {t(`AddBuilding.finalStep.tiers.${tier}.features.${key}`)}
                        </span>
                      </Stack>
                    ))}
                  </Stack>
                  <Button className="mt-8" size="sm" variant="secondary">
                    {t(`AddBuilding.finalStep.tiers.${tier}.cta`)}
                  </Button>
                </Paper>
              ))}
            </div>
          </div>
        </Stack>
      </div>
    </Grid>
  );
};
